// Colours of clothing (docs/plans/sizes-colours.md): the ready ones to tap, patterns, the
// shades behind "Other colour", and the shop's own colours gathered from its groups.
// A colour is its name; its dot is only to tell colours apart and is kept on the group
// (`products.variant_swatches`: a hex colour or `pattern:<name>`).
import type { JsonValue } from '@ims/data'

export interface ReadyColour {
  name: string
  /** `#rrggbb` or `pattern:<name>`. */
  swatch: string
}

export const READY_COLOURS: readonly ReadyColour[] = [
  { name: 'Black', swatch: '#111827' },
  { name: 'White', swatch: '#f8fafc' },
  { name: 'Off-white', swatch: '#f5f0e6' },
  { name: 'Cream', swatch: '#f3e5c0' },
  { name: 'Grey', swatch: '#9ca3af' },
  { name: 'Navy', swatch: '#1e3a8a' },
  { name: 'Blue', swatch: '#2563eb' },
  { name: 'Sky blue', swatch: '#7dd3fc' },
  { name: 'Red', swatch: '#dc2626' },
  { name: 'Maroon', swatch: '#7f1d1d' },
  { name: 'Pink', swatch: '#f472b6' },
  { name: 'Peach', swatch: '#fdba8c' },
  { name: 'Orange', swatch: '#f97316' },
  { name: 'Mustard', swatch: '#ca8a04' },
  { name: 'Yellow', swatch: '#facc15' },
  { name: 'Green', swatch: '#16a34a' },
  { name: 'Olive', swatch: '#65743a' },
  { name: 'Brown', swatch: '#78350f' },
  { name: 'Beige', swatch: '#d6c4a5' },
  { name: 'Purple', swatch: '#7e22ce' },
]

export const PATTERNS: readonly ReadyColour[] = [
  { name: 'Printed', swatch: 'pattern:printed' },
  { name: 'Floral', swatch: 'pattern:floral' },
  { name: 'Checks', swatch: 'pattern:checks' },
  { name: 'Stripes', swatch: 'pattern:stripes' },
  { name: 'Multi', swatch: 'pattern:multi' },
]

const PATTERN_LOOK: Record<string, string> = {
  'pattern:printed':
    'radial-gradient(circle at 30% 30%,#1e3a8a 18%,transparent 20%),radial-gradient(circle at 70% 65%,#dc2626 18%,transparent 20%),#fef3c7',
  'pattern:floral':
    'radial-gradient(circle at 35% 40%,#f472b6 22%,transparent 24%),radial-gradient(circle at 70% 70%,#16a34a 14%,transparent 16%),#fff7ed',
  'pattern:checks': 'repeating-conic-gradient(#1e3a8a 0 25%,#e0e7ff 0 50%) 0 0/8px 8px',
  'pattern:stripes': 'repeating-linear-gradient(45deg,#7f1d1d 0 3px,#fde68a 3px 6px)',
  'pattern:multi': 'conic-gradient(#dc2626,#facc15,#16a34a,#2563eb,#7e22ce,#dc2626)',
}

/** Whether a stored dot is one the server takes (the same rule as `_swatches_ok`). */
export function isSwatch(value: unknown): value is string {
  return typeof value === 'string' && (/^#[0-9a-f]{6}$/.test(value) || value in PATTERN_LOOK)
}

/** The CSS background of a dot. */
export function swatchBackground(swatch: string): string {
  return PATTERN_LOOK[swatch] ?? swatch
}

/** Six hues in five shades, then six greys: the grid behind "Other colour". */
export const SHADES: readonly string[] = (() => {
  const out: string[] = []
  const hues = [0, 28, 48, 140, 215, 275]
  for (const light of [0.85, 0.7, 0.55, 0.4, 0.27])
    for (const h of hues) out.push(hsl(h, 0.7, light))
  out.push('#f8fafc', '#e5e7eb', '#9ca3af', '#6b7280', '#374151', '#111827')
  return out
})()

function hsl(h: number, s: number, l: number): string {
  const k = (n: number) => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))
  const hex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${hex(f(0))}${hex(f(8))}${hex(f(4))}`
}

/** "#7F1D1D" or "7f1d1d" → "#7f1d1d"; anything else → null. */
export function normalHex(text: string): string | null {
  const t = text.trim().toLowerCase().replace(/^#/, '')
  return /^[0-9a-f]{6}$/.test(t) ? `#${t}` : null
}

/** A group's dots by colour name (anything the server would refuse is left out). */
export function readSwatches(value: JsonValue | null | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  if (!value || typeof value !== 'object' || Array.isArray(value)) return out
  for (const [name, swatch] of Object.entries(value)) if (isSwatch(swatch)) out[name] = swatch
  return out
}

/**
 * The shop's own colours, from its groups' dots: the most used first, then by name.
 * A name met with two dots keeps the one used most.
 */
export function shopColours(
  groups: readonly { variant_swatches: JsonValue | null }[],
): ReadyColour[] {
  const counts = new Map<string, { name: string; uses: number; swatches: Map<string, number> }>()
  for (const g of groups) {
    for (const [name, swatch] of Object.entries(readSwatches(g.variant_swatches))) {
      const key = name.trim().toLowerCase()
      const entry = counts.get(key) ?? { name: name.trim(), uses: 0, swatches: new Map() }
      entry.uses += 1
      entry.swatches.set(swatch, (entry.swatches.get(swatch) ?? 0) + 1)
      counts.set(key, entry)
    }
  }
  return [...counts.values()]
    .sort((a, b) => b.uses - a.uses || a.name.localeCompare(b.name))
    .map((e) => ({
      name: e.name,
      swatch: [...e.swatches.entries()].sort((a, b) => b[1] - a[1])[0]![0],
    }))
}

/** A ready colour or pattern by name, for a dot when a typed name matches one. */
export function readySwatch(name: string): string | null {
  const key = name.trim().toLowerCase()
  return [...READY_COLOURS, ...PATTERNS].find((c) => c.name.toLowerCase() === key)?.swatch ?? null
}
